import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import {
  useGetProfileQuery,
  useGetBalanceQuery,
  useGetProviderStatsQuery,
  useGetDashboardStatsQuery,
  useCreateWithdrawalMutation,
} from "../store/api";
import { useLocalStorage } from "../hooks/useLocalStorage";

const DashboardContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: calc(100vh - 120px); /* Учитываем высоту хедера */
  padding: 2rem;
  gap: 2rem; /* Добавляем отступ между сеткой и плашкой периодов */

  @media (max-width: 768px) {
    padding: 1rem;
    gap: 1.5rem;
    min-height: calc(100vh - 100px);
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  width: 100%;
  max-width: 1200px;
  margin-bottom: 0;

  @media (max-width: 768px) {
    display: none;
  }
`;

const RefreshButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border: 1px solid var(--accent-primary);
  border-radius: 0.5rem;
  background: transparent;
  color: var(--accent-primary);
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    background: var(--accent-primary);
    color: #ffffff;
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  @media (max-width: 768px) {
    padding: 0.375rem 0.75rem;
    font-size: 0.8125rem;
  }
`;

const MobileRefreshRow = styled.div`
  display: none;

  @media (max-width: 768px) {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    margin-bottom: 0.25rem;
    flex-shrink: 0;
  }
`;

const MobileRefreshLabel = styled.span`
  font-size: 0.8125rem;
  color: var(--text-secondary);
  font-weight: 500;
`;

const PeriodSelector = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  padding: 1rem 1.5rem;
  background: transparent;
  max-width: 1200px;
  width: 100%;

  @media (max-width: 768px) {
    flex-wrap: wrap;
    padding: 0.75rem 1rem;
    gap: 0.75rem;
  }
`;

const PeriodButton = styled.button<{ isActive: boolean }>`
  padding: 0.5rem 1rem;
  border: 1px solid ${(props) => (props.isActive ? "var(--accent-primary)" : "var(--border-color)")};
  border-radius: 0.5rem;
  background: ${(props) => (props.isActive ? "var(--accent-primary)" : "transparent")};
  color: ${(props) => (props.isActive ? "#ffffff" : "var(--text-primary)")};
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;

  &:hover {
    background: ${(props) =>
      props.isActive ? "var(--accent-primary-hover)" : "var(--bg-card-hover)"};
    border-color: ${(props) => (props.isActive ? "var(--accent-primary-hover)" : "var(--border-input)")};
  }

  @media (max-width: 768px) {
    padding: 0.375rem 0.75rem;
    font-size: 0.8125rem;
  }
`;

const CustomPeriodContainer = styled.div<{ isVisible: boolean }>`
  display: ${(props) => (props.isVisible ? "flex" : "none")};
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem;
  background: var(--bg-glass);
  border-radius: 0.5rem;
  border: 1px solid var(--border-divider);

  @media (max-width: 768px) {
    width: 100%;
    justify-content: center;
  }
`;

const DateInput = styled.input`
  background: transparent;
  border: 1px solid var(--border-divider);
  border-radius: 0.375rem;
  padding: 0.375rem 0.5rem;
  color: var(--text-primary);
  font-size: 0.875rem;
  width: 120px;

  &:focus {
    outline: none;
    border-color: var(--accent-primary);
    box-shadow: var(--focus-ring);
  }

  @media (max-width: 768px) {
    width: 110px;
    font-size: 0.8125rem;
  }
`;

const DateSeparator = styled.span`
  color: var(--text-muted);
  font-weight: 500;
`;

const OrdersGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
  margin-top: 0.75rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 0.75rem;
  }
`;

const OrderMetric = styled.div<{ variant?: "total" | "success" | "failed" }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 1rem;
  background: var(--bg-card-hover);
  border-radius: 0.75rem;
  border: 1px solid var(--border-light);
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: var(--shadow-md);
    background: var(--bg-glass);
  }
`;

const OrderMetricValue = styled.div`
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--text-primary);
  line-height: 1.2;
`;

const OrderMetricLabel = styled.div`
  font-size: 0.75rem;
  color: var(--text-secondary);
  font-weight: 500;
  text-align: center;
  margin-top: 0.25rem;
`;

const ResetButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  border: 1px solid var(--accent-error);
  border-radius: 0.5rem;
  background: var(--bg-card);
  color: var(--accent-error);
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  font-size: 1rem;
  font-weight: 600;
  margin-left: 0.5rem;
  opacity: 0;
  animation: fadeIn 0.3s ease-out forwards;

  &:hover {
    background: var(--alert-error-bg);
    color: var(--accent-error);
    border-color: var(--accent-error);
    transform: translateY(-1px);
    box-shadow: 0 4px 8px var(--accent-error-bg);
  }

  &:active {
    transform: translateY(0) scale(0.95);
  }

  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateX(-10px);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }

  @media (max-width: 768px) {
    width: 1.75rem;
    height: 1.75rem;
    font-size: 0.875rem;
    margin-left: 0.375rem;
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  max-width: 1200px;
  width: 100%;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const StatCard = styled.div`
  background: transparent;
  padding: 2rem 1.5rem;
  border-right: 1px solid var(--border-divider);
  border-bottom: 1px solid var(--border-divider);
  position: relative;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  flex-direction: column;
  gap: 1rem;

  /* Убираем правую границу для последних элементов в строке */
  &:nth-child(3n) {
    border-right: none;
  }

  /* Убираем нижнюю границу для элементов в последней строке */
  &:nth-last-child(-n + 3) {
    border-bottom: none;
  }

  &:hover {
    background: var(--bg-card-hover);
  }

  @media (max-width: 768px) {
    border-right: none;

    &:last-child {
      border-bottom: none;
    }
  }
`;

const StatHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 1rem;
`;

const StatContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const StatValue = styled.div`
  font-size: 2.5rem;
  font-weight: 800;
  color: var(--text-primary);
  line-height: 1.1;
  margin-bottom: 0.5rem;
`;

const StatLabel = styled.div`
  font-size: 0.875rem;
  color: var(--text-secondary);
  font-weight: 600;
  line-height: 1.4;
  flex: 1;
`;

const StatChange = styled.div<{ positive: boolean }>`
  font-size: 0.75rem;
  color: var(--accent-primary);
  font-weight: 600;
  padding: 0.25rem 0.75rem;
  background: var(--accent-primary-light);
  border-radius: 0.5rem;
  border: 1px solid var(--accent-primary-border);
  display: inline-block;
  white-space: nowrap;
  transition: all 0.3s ease;

  &:hover {
    background: var(--accent-primary-glow);
    transform: translateY(-1px);
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const ActionButton = styled.button<{
  disabled?: boolean;
  variant?: "primary" | "secondary";
}>`
  padding: 0.375rem 0.75rem;
  border: none;
  border-radius: 0.375rem;
  font-size: 0.75rem;
  font-weight: 600;
  cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};
  transition: all 0.2s;

  ${({ disabled, variant = "primary" }) => {
    if (disabled) {
      return `
        background: var(--bg-secondary);
        color: var(--text-muted);
        border: 1px solid var(--border-color);
      `;
    }

    if (variant === "secondary") {
      return `
        background: transparent;
        color: var(--accent-primary);
        border: 1px solid var(--accent-primary);
        &:hover {
          background: var(--accent-primary);
          color: #ffffff;
          transform: translateY(-1px);
        }
      `;
    }

    return `
      background: var(--accent-primary);
      color: #ffffff;
      &:hover {
        background: var(--accent-primary-hover);
        transform: translateY(-1px);
      }
    `;
  }}
`;

// Модальное окно вывода
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: var(--bg-modal-overlay);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: var(--bg-card);
  border-radius: 0.75rem;
  padding: 2rem;
  width: 90%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
`;

const ModalTitle = styled.h2`
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: var(--text-muted);

  &:hover {
    color: var(--text-primary);
  }
`;

const FormGroup = styled.div`
  margin-bottom: 1rem;
`;

const Label = styled.label`
  display: block;
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--text-primary);
  margin-bottom: 0.5rem;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid var(--border-input);
  border-radius: 0.5rem;
  font-size: 0.875rem;
  color: var(--text-primary);

  &:focus {
    outline: none;
    border-color: var(--accent-primary);
    box-shadow: var(--focus-ring);
  }
`;

const ErrorText = styled.div`
  color: var(--accent-error);
  font-size: 0.875rem;
  margin-bottom: 1rem;
`;

const InputGroup = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: flex-end;
`;

const MaxButton = styled.button`
  padding: 0.75rem 1rem;
  border: 1px solid var(--accent-primary);
  border-radius: 0.5rem;
  background: transparent;
  color: var(--accent-primary);
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.2s;

  &:hover {
    background: var(--accent-primary);
    color: #ffffff;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const WithdrawBalanceInfo = styled.div`
  background: var(--bg-primary);
  border: 1px solid var(--border-color);
  border-radius: 0.5rem;
  padding: 0.75rem;
  margin-bottom: 1rem;
  font-size: 0.875rem;
  color: var(--text-secondary);
`;

const WarningText = styled.div`
  color: var(--accent-warning);
  font-size: 0.875rem;
  margin-top: 0.5rem;
  font-style: italic;
`;

type PeriodType = "24h" | "7d" | "30d" | "all" | "custom";

function Dashboard() {
  const navigate = useNavigate();
  const { data: profile, refetch: refetchProfile } = useGetProfileQuery();
  const { data: balance, refetch: refetchBalance } = useGetBalanceQuery();

  // Состояние для выбора периода с сохранением в localStorage
  const [selectedPeriod, setSelectedPeriod] = useLocalStorage<PeriodType>(
    "dashboard-selected-period",
    "30d"
  );
  const [customDateFrom, setCustomDateFrom] = useLocalStorage<string>(
    "dashboard-custom-date-from",
    ""
  );
  const [customDateTo, setCustomDateTo] = useLocalStorage<string>(
    "dashboard-custom-date-to",
    ""
  );

  // Состояние для модального окна вывода средств
  const [showWithdrawModal, setShowWithdrawModal] = useState(false);
  const [withdrawForm, setWithdrawForm] = useState({
    amount: "",
    walletAddress: "",
  });
  const [error, setError] = useState("");

  // API хук для создания заявки на вывод
  const [createWithdrawal, { isLoading: isCreating }] =
    useCreateWithdrawalMutation();

  // Получаем параметры для API
  const getPeriodParams = () => {
    switch (selectedPeriod) {
      case "24h":
        return { period: "24h" };
      case "7d":
        return { period: "7d" };
      case "30d":
        return { period: "30d" };
      case "all":
        return { period: "all" };
      case "custom":
        if (customDateFrom && customDateTo) {
          return {
            dateFrom: customDateFrom,
            dateTo: customDateTo,
          };
        }
        return { period: "30d" }; // fallback
      default:
        return { period: "30d" };
    }
  };

  // Получаем параметры для API запроса
  const periodParams = getPeriodParams();
  console.log("📊 Dashboard API params:", periodParams);

  // Используем новый API для получения статистики дашборда
  const {
    data: dashboardStats,
    refetch: refetchDashboardStats,
    isFetching: isFetchingStats,
  } = useGetDashboardStatsQuery(periodParams);

  // Fallback на старый API если новый не работает
  const { data: providerStats } = useGetProviderStatsQuery(
    profile?.provider?.id || ""
  );

  const formatUSDT = (amount: number | undefined | null): string => {
    if (amount === null || amount === undefined || isNaN(amount)) {
      return "0.00 USDT";
    }
    return `${amount.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })} USDT`;
  };

  // Обработчики событий для периодов
  const handlePeriodChange = (period: PeriodType) => {
    console.log("🔄 Changing period to:", period);
    setSelectedPeriod(period);
    if (period !== "custom") {
      setCustomDateFrom("");
      setCustomDateTo("");
    }
  };

  // Эффект для перезапроса данных при изменении периода
  useEffect(() => {
    refetchDashboardStats();
  }, [selectedPeriod, customDateFrom, customDateTo, refetchDashboardStats]);

  // Ручное обновление: статистика дашборда + баланс + профиль
  const handleRefresh = () => {
    refetchDashboardStats();
    refetchBalance();
    refetchProfile();
  };

  const handleCustomDateChange = (type: "from" | "to", value: string) => {
    if (type === "from") {
      setCustomDateFrom(value);
    } else {
      setCustomDateTo(value);
    }
  };

  // Функция для сброса фильтров к дефолтному состоянию
  const handleResetFilters = () => {
    setSelectedPeriod("30d"); // Возвращаем к дефолтному периоду
    setCustomDateFrom("");
    setCustomDateTo("");
  };

  // Проверяем, нужно ли показывать кнопку сброса
  const shouldShowResetButton =
    selectedPeriod !== "30d" || customDateFrom !== "" || customDateTo !== "";

  // Используем данные из нового API если доступны, иначе из старого
  const statsData = dashboardStats || providerStats;
  const totalOrders = statsData?.totalOrders || 0;
  const completedOrders = statsData?.completedOrders || 0;
  const failedOrders = totalOrders - completedOrders;
  // const overallConversionRate = statsData?.successRate || 0; // Unused
  const processedVolume =
    statsData?.processedVolume || statsData?.totalVolume || 0;

  // Обработчики для формы вывода средств
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setWithdrawForm({ ...withdrawForm, [name]: value });
    setError("");
  };

  const handleMaxAmount = () => {
    setWithdrawForm({
      ...withdrawForm,
      amount: availableForWithdraw.toString(),
    });
    setError("");
  };

  const handleWithdrawSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    const amount = parseFloat(withdrawForm.amount);
    if (isNaN(amount) || amount <= 0) {
      setError("Введите корректную сумму");
      return;
    }

    if (amount % 10 !== 0) {
      setError("Сумма должна быть кратной 10 USDT");
      return;
    }

    if (!withdrawForm.walletAddress) {
      setError("Введите адрес кошелька");
      return;
    }

    try {
      await createWithdrawal({
        amount,
        currency: "USDT",
        walletAddress: withdrawForm.walletAddress,
      }).unwrap();

      setShowWithdrawModal(false);
      setWithdrawForm({ amount: "", walletAddress: "" });
    } catch (err: any) {
      setError(err?.data?.message || "Ошибка при создании заявки на вывод");
    }
  };

  // Вычисляем доступную сумму для вывода (рабочий баланс - замороженный)
  const rawAvailableForWithdraw = Math.max(
    0,
    (balance?.balance || 0) - (balance?.frozenBalance || 0)
  );
  // Округляем вниз до кратной 10 для вывода
  const availableForWithdraw = Math.floor(rawAvailableForWithdraw / 10) * 10;

  return (
    <DashboardContainer>
      {/* Mobile: подпись + кнопка обновления над сеткой */}
      <MobileRefreshRow>
        <MobileRefreshLabel>Статистика за период</MobileRefreshLabel>
        <RefreshButton
          onClick={handleRefresh}
          disabled={isFetchingStats}
          aria-label="Обновить данные дашборда"
          style={{ padding: "0.25rem 0.625rem", fontSize: "0.75rem" }}
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            aria-hidden="true"
          >
            <path d="M21 2v6h-6" />
            <path d="M3 12a9 9 0 0 1 15-6.7L21 8" />
            <path d="M3 22v-6h6" />
            <path d="M21 12a9 9 0 0 1-15 6.7L3 16" />
          </svg>
          {isFetchingStats ? "..." : "Обновить"}
        </RefreshButton>
      </MobileRefreshRow>

      {/* Desktop: кнопка обновления в правой части хедера над сеткой */}
      <Header>
        <RefreshButton
          onClick={handleRefresh}
          disabled={isFetchingStats}
          aria-label="Обновить данные дашборда"
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            aria-hidden="true"
          >
            <path d="M21 2v6h-6" />
            <path d="M3 12a9 9 0 0 1 15-6.7L21 8" />
            <path d="M3 22v-6h6" />
            <path d="M21 12a9 9 0 0 1-15 6.7L3 16" />
          </svg>
          {isFetchingStats ? "Обновление..." : "Обновить"}
        </RefreshButton>
      </Header>

      <StatsGrid>
        <StatCard>
          <StatHeader>
            <StatLabel>Рабочий баланс</StatLabel>
            <ButtonGroup>
              <ActionButton onClick={() => navigate("/balance")}>
                Пополнить
              </ActionButton>
              <ActionButton
                variant="secondary"
                onClick={() => setShowWithdrawModal(true)}
              >
                Вывести
              </ActionButton>
            </ButtonGroup>
          </StatHeader>
          <StatContent>
            <StatValue>{formatUSDT(balance?.balance || 0)}</StatValue>
            <div
              style={{
                fontSize: "0.75rem",
                color: "var(--text-secondary)",
                marginTop: "0.25rem",
              }}
            >
              В диспутах: {formatUSDT(balance?.disputeBalance || 0)}
              <br />
              Заморожено: {formatUSDT(balance?.frozenBalance || 0)}
            </div>
          </StatContent>
        </StatCard>

        {/* Переходный баланс — только для DEPOSIT режима с включённым переходным балансом */}
        {balance?.balanceMode !== "SETTLE" && (
        <StatCard>
          <StatHeader>
            <StatLabel>Переходный баланс</StatLabel>
            <ActionButton disabled>Вывести</ActionButton>
          </StatHeader>
          <StatContent>
            <StatValue>{formatUSDT(balance?.transitBalance || 0)}</StatValue>
            <div
              data-testid="transit-threshold"
              style={{
                fontSize: "0.75rem",
                color: "var(--text-secondary)",
                marginTop: "0.25rem",
                fontWeight: 600,
              }}
            >
              / {formatUSDT(balance?.transitBalanceThreshold ?? 0)}
            </div>
            <div
              style={{
                fontSize: "0.75rem",
                color: "var(--text-secondary)",
                marginTop: "0.25rem",
              }}
            >
              {balance?.transitBalanceEnabled
                ? "Доступно через 30 дней"
                : "Переходный баланс отключён"}
            </div>
          </StatContent>
        </StatCard>
        )}

        {/* Для SETTLE режима — информация о долге */}
        {balance?.balanceMode === "SETTLE" && (
        <StatCard>
          <StatHeader>
            <StatLabel>Режим: Settle</StatLabel>
            <StatChange positive={(balance?.balance || 0) >= 0}>
              {(balance?.balance || 0) >= 0 ? "Без долга" : "Есть долг"}
            </StatChange>
          </StatHeader>
          <StatContent>
            <StatValue>
              {formatUSDT(Math.abs(Math.min(0, balance?.balance || 0)))}
            </StatValue>
            <div
              style={{
                fontSize: "0.75rem",
                color: "var(--text-secondary)",
                marginTop: "0.25rem",
              }}
            >
              Порог: {formatUSDT(balance?.settleThreshold || 0)}
            </div>
          </StatContent>
        </StatCard>
        )}

        <StatCard>
          <StatHeader>
            <StatLabel>Статистика ордеров</StatLabel>
          </StatHeader>
          <StatContent>
            <OrdersGrid>
              <OrderMetric variant="total">
                <OrderMetricValue>{totalOrders}</OrderMetricValue>
                <OrderMetricLabel>Всего</OrderMetricLabel>
              </OrderMetric>
              <OrderMetric variant="success">
                <OrderMetricValue>{completedOrders}</OrderMetricValue>
                <OrderMetricLabel>Успешных</OrderMetricLabel>
              </OrderMetric>
              <OrderMetric variant="failed">
                <OrderMetricValue>{failedOrders}</OrderMetricValue>
                <OrderMetricLabel>Отменённых</OrderMetricLabel>
              </OrderMetric>
            </OrdersGrid>
          </StatContent>
        </StatCard>

        <StatCard>
          <StatHeader>
            <StatLabel>Обработанный объем</StatLabel>
          </StatHeader>
          <StatContent>
            <StatValue>{formatUSDT(processedVolume)}</StatValue>
            <div
              style={{
                fontSize: "0.75rem",
                color: "var(--text-secondary)",
                marginTop: "0.25rem",
              }}
            >
              Успешно обработанные ордера
            </div>
          </StatContent>
        </StatCard>

        <StatCard>
          <StatHeader>
            <StatLabel>Прибыль</StatLabel>
            <StatChange positive={true}>
              Заработано за период
            </StatChange>
          </StatHeader>
          <StatContent>
            <StatValue style={{ color: 'var(--accent-success)' }}>
              {formatUSDT(statsData?.providerProfit || statsData?.totalCommissions || 0)}
            </StatValue>
            <div
              style={{
                fontSize: "0.75rem",
                color: "var(--text-secondary)",
                marginTop: "0.25rem",
              }}
            >
              Комиссия с {completedOrders} завершённых ордеров
            </div>
          </StatContent>
        </StatCard>

        <StatCard>
          <StatHeader>
            <StatLabel>Процент апелляций</StatLabel>
            <StatChange positive={false}>
              Тех. апелляции / общие транзакции
            </StatChange>
          </StatHeader>
          <StatContent>
            <StatValue>{(0).toFixed(1)}%</StatValue>
            <div
              style={{
                fontSize: "0.75rem",
                color: "var(--text-secondary)",
                marginTop: "0.25rem",
              }}
            >
              Тех. апелляции: 0 | Транзакции: {totalOrders}
            </div>
          </StatContent>
        </StatCard>

        <StatCard>
          <StatHeader>
            <StatLabel>Выдача</StatLabel>
            <StatChange positive={true}>
              Выданы реки / входящие ордера
            </StatChange>
          </StatHeader>
          <StatContent>
            <StatValue>{(0).toFixed(1)}%</StatValue>
            <div
              style={{
                fontSize: "0.75rem",
                color: "var(--text-secondary)",
                marginTop: "0.25rem",
              }}
            >
              Выдано реков: 0 | Входящие: {totalOrders}
            </div>
          </StatContent>
        </StatCard>
      </StatsGrid>

      {/* Селектор периодов */}
      <PeriodSelector>
        <PeriodButton
          isActive={selectedPeriod === "24h"}
          onClick={() => handlePeriodChange("24h")}
        >
          24 часа
        </PeriodButton>

        <PeriodButton
          isActive={selectedPeriod === "7d"}
          onClick={() => handlePeriodChange("7d")}
        >
          7 дней
        </PeriodButton>

        <PeriodButton
          isActive={selectedPeriod === "30d"}
          onClick={() => handlePeriodChange("30d")}
        >
          30 дней
        </PeriodButton>

        <PeriodButton
          isActive={selectedPeriod === "all"}
          onClick={() => handlePeriodChange("all")}
        >
          Все время
        </PeriodButton>

        <PeriodButton
          isActive={selectedPeriod === "custom"}
          onClick={() => handlePeriodChange("custom")}
        >
          Период
        </PeriodButton>

        {/* Тайм пикер для кастомного периода */}
        <CustomPeriodContainer isVisible={selectedPeriod === "custom"}>
          <DateInput
            type="date"
            value={customDateFrom}
            onChange={(e) => handleCustomDateChange("from", e.target.value)}
            max={customDateTo || undefined}
          />
          <DateSeparator>—</DateSeparator>
          <DateInput
            type="date"
            value={customDateTo}
            onChange={(e) => handleCustomDateChange("to", e.target.value)}
            min={customDateFrom || undefined}
          />
        </CustomPeriodContainer>

        {/* Кнопка сброса - показывается только когда есть активные фильтры */}
        {shouldShowResetButton && (
          <ResetButton
            onClick={handleResetFilters}
            title="Сбросить фильтры периода"
          >
            ×
          </ResetButton>
        )}
      </PeriodSelector>

      {/* Модальное окно вывода */}
      {showWithdrawModal && (
        <ModalOverlay>
          <ModalContent>
            <ModalHeader>
              <ModalTitle>Вывод средств</ModalTitle>
              <CloseButton onClick={() => setShowWithdrawModal(false)}>
                ×
              </CloseButton>
            </ModalHeader>

            <form onSubmit={handleWithdrawSubmit}>
              <WithdrawBalanceInfo>
                <div>
                  <strong>Рабочий баланс:</strong>{" "}
                  {formatUSDT(rawAvailableForWithdraw)}
                </div>
                <div>
                  <strong>Доступно к выводу:</strong>{" "}
                  {formatUSDT(availableForWithdraw)}
                </div>
                {rawAvailableForWithdraw !== availableForWithdraw && (
                  <WarningText>
                    Остаток{" "}
                    {formatUSDT(rawAvailableForWithdraw - availableForWithdraw)}{" "}
                    останется на балансе (сумма должна быть кратна 10 USDT)
                  </WarningText>
                )}
              </WithdrawBalanceInfo>

              <FormGroup>
                <Label>Сумма вывода (USDT)</Label>
                <InputGroup>
                  <Input
                    type="number"
                    name="amount"
                    value={withdrawForm.amount}
                    onChange={handleInputChange}
                    placeholder="Введите сумму (кратную 10)"
                    step="10"
                    min="10"
                    max={availableForWithdraw}
                    style={{ flex: 1 }}
                  />
                  <MaxButton
                    type="button"
                    onClick={handleMaxAmount}
                    disabled={availableForWithdraw <= 0}
                  >
                    Максимум
                  </MaxButton>
                </InputGroup>
              </FormGroup>

              <FormGroup>
                <Label>Адрес кошелька</Label>
                <Input
                  type="text"
                  name="walletAddress"
                  value={withdrawForm.walletAddress}
                  onChange={handleInputChange}
                  placeholder="Введите адрес USDT кошелька"
                />
              </FormGroup>

              {error && <ErrorText>{error}</ErrorText>}

              <ButtonGroup>
                <ActionButton
                  type="button"
                  variant="secondary"
                  onClick={() => setShowWithdrawModal(false)}
                >
                  Отмена
                </ActionButton>
                <ActionButton type="submit" disabled={isCreating}>
                  {isCreating ? "Создание..." : "Создать заявку"}
                </ActionButton>
              </ButtonGroup>
            </form>
          </ModalContent>
        </ModalOverlay>
      )}
    </DashboardContainer>
  );
}

export default Dashboard;
